import { FC, useEffect, useState } from 'react'
import { ErrorBoundary, featureToggle, reactAppEnabled } from '@reapit-sales/utilities'
import { useReapitConnect } from '@reapit/connect-session'
import { reapitConnectBrowserSession } from '@reapit-sales/reapit-connect'
import axios from 'axios'
import { useMutation } from '@tanstack/react-query'
import { LegacyCRMIframeID } from './Constants'
import { Loader } from '@reapit/elements'
import { useLegacyIframesVisibilityContext } from './LegacyCRMIframeContext'
import './styles/LegacyCRMStyles.css'
import { useLegacyCRMUrl } from './hooks'
import { NetworkErrorPage } from '../error-pages'
import { Settings, useUserConfigurationsContext } from '@reapit-sales/api-v2'

interface AuthResponse {
  token: string
}

export const LegacyCRMIframe: FC = () => {
  // Get APIv2 Configuration -> Settings
  const { settings } = useUserConfigurationsContext()
  // State to get and set CRM Security Token to authenticate user session in iframe
  const [crmSecurityToken, setCrmSecurityToken] = useState<string | null>(null)
  // State to track network errors
  const [networkError, setNetworkError] = useState<boolean>(false)
  // New state to track legacy CRM security token fetching
  const [isFetchingToken, setIsFetchingToken] = useState<boolean>(true)
  // Context API state to set CRM is loaded
  const { isLegacyCRMIframeLoaded, setIsLegacyCRMIframeLoaded, isLegacyCRMIframeVisible } =
    useLegacyIframesVisibilityContext()

  // Reapit Connect session for obtaining the access token
  const { connectSession } = useReapitConnect(reapitConnectBrowserSession)
  const accessToken = connectSession?.accessToken || ''

  // Get Legacy CRM CLient URL
  const legacyCRMUrl = useLegacyCRMUrl()

  // Local userID - Local environment only
  const localUserEmail = import.meta.env.VITE_LOCAL_LOGGED_IN_USER_ID

  const fetchSecurityToken = async (reapitAccessToken: string): Promise<AuthResponse> => {
    const requestBody: Record<string, string> = {
      token: reapitAccessToken,
    }

    if (localUserEmail) {
      requestBody.email = localUserEmail
    }

    const response = await axios.post(`${legacyCRMUrl}/api/crm/auth`, requestBody)
    return response.data
  }

  const mutation = useMutation({
    mutationFn: () => fetchSecurityToken(accessToken),
    onSuccess: (data) => {
      setCrmSecurityToken(data.token) // Store the token on success
      setIsFetchingToken(false) // Mark token fetching as complete
    },
    onError: (error: any) => {
      console.error('Error creating session:', error.message) // Handle error
      if (axios.isAxiosError(error) && !error.response) {
        setNetworkError(true)
      }
      setIsFetchingToken(false) // Mark token fetching as complete even on error
    },
  })

  useEffect(() => {
    if (accessToken) {
      setNetworkError(false) // Reset network error state while switching offices
      setIsFetchingToken(true) // Start fetching token
      mutation.mutate()
    }
  }, [accessToken, legacyCRMUrl])
  if (
    networkError ||
    (!featureToggle(reactAppEnabled, (settings || {}) as Settings) && process.env.APP_ENV === 'prod')
  ) {
    return <NetworkErrorPage />
  }

  return (
    <ErrorBoundary>
      {/* To do: Add ErrorHandling for API error */}
      {(isFetchingToken || mutation.isPending || !isLegacyCRMIframeLoaded) && <Loader fullPage />}
      {crmSecurityToken && !isFetchingToken && (
        <div className={`legacy-crm-iframe-container ${isLegacyCRMIframeVisible ? '' : 'hide-legacy-crm-iframe'}`}>
          <iframe
            key={legacyCRMUrl}
            id={LegacyCRMIframeID}
            src={`${legacyCRMUrl}/login/crm/${crmSecurityToken}`}
            onLoad={() => setIsLegacyCRMIframeLoaded(true)}
          />
        </div>
      )}
    </ErrorBoundary>
  )
}
